import T from 'Components/Helpers/translate';

/**
 * SNI codes are 5-digit, zero-padded (e.g. "01199"). Codes below 10000 are
 * frequently stored without their leading zero (e.g. "1199") because the source
 * column is numeric, which drops it. Pad purely-numeric codes shorter than 5
 * digits back to 5 so they match the enum and lang keys. Anything else (already
 * 5 digits, or non-numeric) is returned untouched.
 */
export const normalizeSniCode = (code: string): string =>
	/^\d{1,4}$/.test(code) ? code.padStart(5, '0') : code;

/**
 * Translates an SNI code with fallback logic.
 * First tries the sniCode2025 namespace.
 * If no translation is found (the key is returned as-is), falls back to the sniCode namespace.
 *
 * @param code - The SNI code to translate (e.g., "45110")
 * @returns The translated SNI code description, or the code itself if no translation is found
 */
export const translateSniCode = (code: string | number | null | undefined): string => {
	if (!code && code !== 0) {
		return '';
	}

	// Convert to string, clean the code (remove any spaces, take the first part)
	// and pad sub-10000 codes back to their canonical 5-digit form.
	const cleanCode = normalizeSniCode(String(code).split(' ')[0]);

	const sni2025Key = `default.sniCode2025.${cleanCode}`;
	const translation2025 = T(sni2025Key);

	// If the translation is the same as the key, it means no translation was found
	// Fall back to the old namespace
	if (translation2025 === sni2025Key) {
		const sniKey = `default.sniCode.${cleanCode}`;
		const translationOld = T(sniKey);

		// If the old namespace also has no translation, return the 2025 key
		// (this matches the original behavior of showing the key)
		return translationOld === sniKey ? sni2025Key : translationOld;
	}

	return translation2025;
};

/**
 * Gets the translation key for an SNI code with fallback logic.
 * Similar to translateSniCode but returns the key instead of the translated value.
 * This is useful for cases where the translation happens later.
 *
 * @param code - The SNI code (e.g., "45110")
 * @returns The translation key to use
 */
export const getSniCodeTranslationKey = (code: string | number | null | undefined): string => {
	if (!code && code !== 0) {
		return '';
	}

	// Convert to string, clean the code (remove any spaces, take the first part)
	// and pad sub-10000 codes back to their canonical 5-digit form.
	const cleanCode = normalizeSniCode(String(code).split(' ')[0]);

	const sni2025Key = `default.sniCode2025.${cleanCode}`;
	const translation2025 = T(sni2025Key);

	// If no translation found in 2025 namespace, check old namespace
	if (translation2025 === sni2025Key) {
		const sniKey = `default.sniCode.${cleanCode}`;
		const translationOld = T(sniKey);

		// Return the key that has a valid translation
		return translationOld !== sniKey ? sniKey : sni2025Key;
	}

	return sni2025Key;
};
